import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm, Resolver } from 'react-hook-form'
import { z } from 'zod'
import { updateUserFormSchema, UpdateUserFormType, UpdateUserFormInputType } from '../../schemas/update-user.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { updateUser, getVolunteerTableRows } from '../../actions/volunteers.actions'
import errorToast from '@/shared/lib/toast/error.toast'
import successToast from '@/shared/lib/toast/success.toast'
import { useQueryClient } from '@tanstack/react-query'
import { ADMIN } from '@/shared/constants/backend-api'
import { otherLanguageOptions } from '@/features/auth/constants/select-options'
import { NonHomeownerUser } from '../../types/volunteers.type'
import StickySaveBar from '@/shared/components/sticky-save-bar'
import UserBaseForm from './user-base-form'
import { isNoEmailPlaceholderValue } from '../../schemas/volunteers.schema'
import { parseServePeriodBoundary } from '../../lib/merge-serve-periods-for-form'
import { sortServePeriodsChronologically } from '../../lib/serve-periods-display'
import { stripMaskedPiiFromPayload } from '@/shared/lib/pii-utils'

type UpdateUserFormProps = {
    onSuccessCallback?: () => void;
    user: NonHomeownerUser
}

function UpdateUserForm({ onSuccessCallback, user }: UpdateUserFormProps) {
    const queryClient = useQueryClient()
    const canEditEmailOnUpdate = isNoEmailPlaceholderValue(user?.email)

    const defaultValues = useMemo((): UpdateUserFormInputType => {
        // Get status from volunteer record if it exists, default to true (Active)
        const status: 'Active' | 'Inactive' = user?.volunteer?.status !== undefined 
            ? (user.volunteer.status ? 'Active' : 'Inactive')
            : 'Active'
        
        // Convert dateOfBirth string from backend (YYYY-MM-DD) to Date for the form.
        // Skip masked values (e.g. **/**/1990) — those are displayed via RevealablePiiField.
        let dateOfBirthDate: Date | undefined = undefined
        if (user?.dateOfBirth && !String(user.dateOfBirth).includes('*')) {
            const dateStr = user.dateOfBirth.split('T')[0]
            const [year, month, day] = dateStr.split('-').map(Number)
            if (year && month && day) {
                dateOfBirthDate = new Date(year, month - 1, day)
            }
        }

        let backgroundCheckDate: Date | undefined = undefined
        const bgRaw = (user as any)?.volunteer?.backgroundCheck
        if (bgRaw) {
            const dateStr = String(bgRaw).split('T')[0]
            const [by, bm, bd] = dateStr.split('-').map(Number)
            if (by && bm && bd) {
                backgroundCheckDate = new Date(by, bm - 1, bd)
            }
        }

        return {
            fullname: user?.fullname || "",
            // Keep stored placeholder value so read-only display can show "No Email Available".
            // Edit flow still clears this value when user taps "Edit email".
            email: user?.email || "",
            organizationName: (user as any)?.volunteer?.organizationName ?? "",
            preferredLocation: (user as any)?.volunteer?.preferredLocation ?? "",
            profilePicture: (user as any)?.profilePicture ?? undefined,
            gender: (user as any)?.gender ?? (user as any)?.volunteer?.gender ?? undefined,
            tShirtSize: (user as any)?.tShirtSize ?? (user as any)?.volunteer?.tShirtSize ?? undefined,
            backgroundCheck: backgroundCheckDate,
            passportNumber: (user as any)?.passportNumber ?? (user as any)?.volunteer?.passportNumber ?? undefined,
            emergencyContact1FullName: (user as any)?.emergencyContact1FullName ?? (user as any)?.volunteer?.emergencyContact1FullName ?? undefined,
            emergencyContact1Relationship: (user as any)?.emergencyContact1Relationship ?? (user as any)?.volunteer?.emergencyContact1Relationship ?? undefined,
            emergencyContact1Phone: (user as any)?.emergencyContact1Phone ?? (user as any)?.volunteer?.emergencyContact1Phone ?? undefined,
            emergencyContact2FullName: (user as any)?.emergencyContact2FullName ?? (user as any)?.volunteer?.emergencyContact2FullName ?? undefined,
            emergencyContact2Relationship: (user as any)?.emergencyContact2Relationship ?? (user as any)?.volunteer?.emergencyContact2Relationship ?? undefined,
            emergencyContact2Phone: (user as any)?.emergencyContact2Phone ?? (user as any)?.volunteer?.emergencyContact2Phone ?? undefined,
            healthInsuranceProvider: (user as any)?.healthInsuranceProvider ?? (user as any)?.volunteer?.healthInsuranceProvider ?? undefined,
            healthInsurancePhone: (user as any)?.healthInsurancePhone ?? (user as any)?.volunteer?.healthInsurancePhone ?? undefined,
            healthInsurancePolicyNumber: (user as any)?.healthInsurancePolicyNumber ?? (user as any)?.volunteer?.healthInsurancePolicyNumber ?? undefined,
            travelInsuranceProvider: (user as any)?.travelInsuranceProvider ?? (user as any)?.volunteer?.travelInsuranceProvider ?? undefined,
            travelInsurancePhone: (user as any)?.travelInsurancePhone ?? (user as any)?.volunteer?.travelInsurancePhone ?? undefined,
            travelInsurancePolicyNumber: (user as any)?.travelInsurancePolicyNumber ?? (user as any)?.volunteer?.travelInsurancePolicyNumber ?? undefined,
            jamaicaC5ApplicationComplete: (user as any)?.volunteer?.jamaicaC5ApplicationComplete === true,
            jamaicaTripFlightDetails:
                (user as any)?.jamaicaTripFlightDetails ?? (user as any)?.volunteer?.jamaicaTripFlightDetails ?? undefined,
            healthConditions: (user as any)?.healthConditions ?? (user as any)?.volunteer?.healthConditions ?? undefined,
            dietaryRestrictions: (user as any)?.dietaryRestrictions ?? (user as any)?.volunteer?.dietaryRestrictions ?? undefined,
            vehicleMake: (user as any)?.vehicleMake ?? (user as any)?.volunteer?.vehicleMake ?? undefined,
            vehicleModel: (user as any)?.vehicleModel ?? (user as any)?.volunteer?.vehicleModel ?? undefined,
            vehicleColor: (user as any)?.vehicleColor ?? (user as any)?.volunteer?.vehicleColor ?? undefined,
            vehicleLicensePlateNumber: (user as any)?.vehicleLicensePlateNumber ?? (user as any)?.volunteer?.vehicleLicensePlateNumber ?? undefined,
            vehicleLicensePlateState: (user as any)?.vehicleLicensePlateState ?? (user as any)?.volunteer?.vehicleLicensePlateState ?? undefined,
            selectedRegions: (user as any)?.selectedRegions?.length
                ? (user as any).selectedRegions.map((id: number) => String(id))
                : user?.selectedRegion
                    ? [String(user.selectedRegion)]
                    : [],
            phone: user?.phone || "",
            role: user?.role?.id?.toString() || "4", // Convert to string for select input
            status: status,
            experienceConstruction: (user as any)?.volunteer?.experienceConstruction ?? undefined,
            experienceDataManagement: (user as any)?.volunteer?.experienceDataManagement ?? undefined,
            experienceStorytelling: (user as any)?.volunteer?.experienceStorytelling ?? undefined,
            mealPreparationAbility: (() => {
                const mp = (user as any)?.volunteer?.mealPreparationAbility
                if (typeof mp === 'number' && mp >= 1 && mp <= 5) return mp
                if (typeof mp === 'string' && mp.trim() !== '') {
                    const n = parseInt(mp, 10)
                    if (!isNaN(n) && n >= 1 && n <= 5) return n
                }
                return undefined
            })(),
            experienceLeadingTeams: (user as any)?.volunteer?.experienceLeadingTeams ?? undefined,
            experienceDrywallFinishing: (user as any)?.volunteer?.experienceDrywallFinishing ?? undefined,
            experienceRoughCarpentry: (user as any)?.volunteer?.experienceRoughCarpentry ?? undefined,
            experienceFinishCarpentry: (user as any)?.volunteer?.experienceFinishCarpentry ?? undefined,
            experienceFlooring: (user as any)?.volunteer?.experienceFlooring ?? undefined,
            experienceWoodWorking: (user as any)?.volunteer?.experienceWoodWorking ?? undefined,
            experienceElectrical: (user as any)?.volunteer?.experienceElectrical ?? undefined,
            experiencePlumbing: (user as any)?.volunteer?.experiencePlumbing ?? undefined,
            experienceHvac: (user as any)?.volunteer?.experienceHvac ?? undefined,
            experienceArborist: (user as any)?.volunteer?.experienceArborist ?? undefined,
            experienceRoofing: (user as any)?.volunteer?.experienceRoofing ?? undefined,
            experienceHeavyEquipment: (user as any)?.volunteer?.experienceHeavyEquipment ?? undefined,
            experienceManualLabor: (user as any)?.volunteer?.experienceManualLabor ?? undefined,
            experienceAltMinistries: (user as any)?.volunteer?.experienceAltMinistries ?? undefined,
            skillsAndQualities: (user as any)?.volunteer?.skillsAndQualities ?? "",
            otherLanguages: (() => {
                const raw = (user as any)?.volunteer?.otherLanguages
                if (raw === null || raw === undefined || raw === '') return undefined
                const validValues = otherLanguageOptions.map((o) => o.value)
                return validValues.includes(raw as (typeof validValues)[number]) ? raw : undefined
            })(),
            dateOfBirth: dateOfBirthDate,
            hasServedBefore: (() => {
                const raw = (user as any)?.volunteer?.hasServedBefore
                if (raw == null || String(raw).trim() === '') return undefined
                const s = String(raw).trim().toLowerCase()
                if (s === 'yes') return 'yes'
                if (s === 'no') return 'no'
                return undefined
            })(),
            isChristFollower: (() => {
                const raw = (user as any)?.volunteer?.isChristFollower
                if (raw == null || String(raw).trim() === '') return ''
                const s = String(raw).trim().toLowerCase()
                if (s === 'yes') return 'Yes'
                if (s === 'no') return 'No'
                return ''
            })(),
            chatNotifications: user?.chatNotifications ?? true,
            workOrderNotifications: user?.workOrderNotifications ?? true,
            emailUpdates: canEditEmailOnUpdate ? false : (user?.emailUpdates ?? true),
            // Initialize servePeriods from volunteers.serveDates
            servePeriods: (() => {
                const serveDates = user?.volunteer?.serveDates
                
                if (serveDates && Array.isArray(serveDates) && serveDates.length > 0) {
                    const mapped = serveDates.map((period: any) => {
                        const regionId = period.regionId != null && period.regionId !== ''
                            ? (typeof period.regionId === 'number' ? period.regionId : parseInt(period.regionId, 10))
                            : undefined
                        return {
                            from: parseServePeriodBoundary(period.from),
                            to: parseServePeriodBoundary(period.to),
                            regionId: regionId != null && !isNaN(regionId) ? String(regionId) : undefined,
                        }
                    })
                    return sortServePeriodsChronologically(mapped)
                }
                return [{ from: undefined, to: undefined, regionId: undefined }]
            })(),
        }
    }, [user, canEditEmailOnUpdate])

    const form = useForm<z.input<typeof updateUserFormSchema>, unknown, UpdateUserFormType>({
        resolver: zodResolver(updateUserFormSchema) as Resolver<z.input<typeof updateUserFormSchema>, unknown, UpdateUserFormType>,
        defaultValues: defaultValues as z.input<typeof updateUserFormSchema>,
    })
    const { handleSubmit, formState: { isSubmitting, isDirty, dirtyFields }, reset, getValues } = form
    const [showSuccessBar, setShowSuccessBar] = useState(false)

    // Reset form to clear dirty state on mount
    useEffect(() => {
        reset(defaultValues)
    }, [reset, defaultValues])

    // Hide success bar when form becomes dirty again
    useEffect(() => {
        if (isDirty && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [isDirty, showSuccessBar])

    const onSubmit = useCallback(async (data: UpdateUserFormType) => {
        const normalizedEmail = String(data.email ?? '').trim().toLowerCase()
        const hasTypedEmailForPlaceholder =
            canEditEmailOnUpdate && !!dirtyFields.email && normalizedEmail.length > 0
        const emailChanged = hasTypedEmailForPlaceholder || (!!dirtyFields.email && normalizedEmail.length > 0)

        // Frontend guard: prevent "saved" toast when email already belongs to another user.
        // This gives immediate, clear feedback without requiring backend changes.
        if (emailChanged) {
            const lookup = await getVolunteerTableRows({ search: normalizedEmail, page: 1, limit: 20 })
            const duplicate = lookup.success && Array.isArray(lookup.data?.users)
                ? lookup.data.users.find((u) =>
                    String((u as any).email ?? '').trim().toLowerCase() === normalizedEmail &&
                    Number((u as any).id) !== Number(user.id)
                )
                : undefined
            if (duplicate) {
                errorToast('This email is already used by another volunteer.')
                return
            }
        }

        const payload: UpdateUserFormType = stripMaskedPiiFromPayload({ ...data })
        // Cleared date inputs become undefined; send null so volunteers.backgroundCheck can be cleared in DB
        if (dirtyFields.backgroundCheck && data.backgroundCheck === undefined) {
            ;(payload as { backgroundCheck?: string | null }).backgroundCheck = null
        }
        // Never send untouched serve date/region fields; backend keeps existing values.
        if (!dirtyFields.servePeriods) {
            delete (payload as any).servePeriods
        }
        if (!dirtyFields.selectedRegions) {
            delete (payload as any).selectedRegions
        }
        if (hasTypedEmailForPlaceholder) {
            // Ensure typed email is submitted even if RHF dirty tracking didn't mark it.
            payload.email = normalizedEmail
        } else if (!dirtyFields.email) {
            delete (payload as any).email
        }

        const response = await updateUser(user.id, payload)
        if (!response.success) {
            errorToast(response.message || 'Failed to update volunteer')
            return
        }
        successToast("Volunteer updated successfully")
        queryClient.invalidateQueries({ queryKey: [ADMIN.base] })
        queryClient.invalidateQueries({ queryKey: [ADMIN.tableRows] })
        // Reset form with current form values to clear submitting state
        const currentValues = getValues()
        reset(currentValues)
        setShowSuccessBar(true)
        if (onSuccessCallback) {
            onSuccessCallback()
        }
    }, [user.id, queryClient, reset, getValues, onSuccessCallback, dirtyFields.email, dirtyFields.servePeriods, dirtyFields.selectedRegions, canEditEmailOnUpdate])

    const handleStickySave = useCallback(() => {
        handleSubmit(onSubmit)()
    }, [handleSubmit, onSubmit])

    const showBar = isDirty || showSuccessBar

    return (
        <FormProvider {...form}>
            <div className="relative">
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                >
                    <UserBaseForm
                    isUpdate={true}
                    canEditEmailOnUpdate={canEditEmailOnUpdate}
                    initialVolunteerRegionId={user.volunteer?.regionId ?? null}
                    compactTravelUi
                    rawUserData={user as Record<string, unknown>}
                />
                </form>
                {/* Sticky at bottom of dialog scroll area so bar stays visible when scrolling */}
                {showBar && (
                    <div className="sticky bottom-0 left-0 right-0 z-10 min-h-[42px] relative -mx-5 md:-mx-8 mt-4">
                        <StickySaveBar
                            isVisible={true}
                            isSuccess={showSuccessBar}
                            isSubmitting={isSubmitting}
                            onSave={handleStickySave}
                            position="container"
                        />
                    </div>
                )}
            </div>
        </FormProvider>
    )
}

export default UpdateUserForm
